<template>
	<div v-if="link" class="flex items-end gap-3">
		<LabeledObject full>
			<template #label>{{ trans(`account.types.${link.account_level}`) }}</template>
			<TextInput v-model="link.link"/>
		</LabeledObject>
		<PrimaryButton :color="Color.Green" @click.stop="update">
			<i class="fa-solid fa-check"/>
		</PrimaryButton>
		<PrimaryButton :color="Color.Red" @click.stop="destroy">
			<i class="fa-solid fa-trash"/>
		</PrimaryButton>
	</div>
	<div v-else class="flex items-end gap-3">
		<LabeledObject full>
			<template #label>{{ trans(`account.types.${newPaymentLink.account_level}`) }}</template>
			<TextInput v-model="newPaymentLink.link"/>
		</LabeledObject>
		<PrimaryButton :color="Color.Green" @click.stop="store">
			<i class="fa-solid fa-plus"/>
		</PrimaryButton>
	</div>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import {AccountLevel} from '@/Types/Enums/AccountLevel'
import TextInput from '@/Components/UI/Inputs/TextInput.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {PaymentLinkType} from '@/Types/Enums/PaymentLinkType'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaymentLink} from '@/Types/Events/Payments/PaymentLink'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	eventId:number,
	link?:PaymentLink,
	type:PaymentLinkType,
	accountLevel:AccountLevel,
}>()

const emit = defineEmits([
	'stored',
	'destroyed',
])

const type:Ref<PaymentLinkType> = ref(props.type)
const link:Ref<PaymentLink|undefined> = ref(props.link)
const accountLevel:Ref<AccountLevel> = ref(props.accountLevel)
const newPaymentLink:Ref<PaymentLink> = ref({
	event_id: props.eventId,
	type: type.value,
	account_level: accountLevel.value,
	link: '',
} as PaymentLink)
const working:Ref<boolean> = ref(false)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.payments.payment-links.store', newPaymentLink.value.event_id), newPaymentLink.value).then(({data}:{data:PaymentLink}) => {
			showSuccess(trans('general.inserted'))
			emit('stored', data)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function update():void{
	if(!working.value && link.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.payments.payment-links.update', {event: props.eventId, paymentLink: link.value.id}), link.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function destroy():void{
	if(!working.value && link.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.payments.payment-links.destroy', {event: props.eventId, paymentLink: link.value.id})).then(() => {
			showSuccess(trans('general.deleted'))
			emit('destroyed', link.value)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>